import { useCallback, useRef, useState } from "react";

import { useGlobalContext } from "../Context/GlobalContext";

// =========================================================
// CLEAN IMAGE URL
// =========================================================

const cleanImageUrl = (imageUrl) => {
  if (!imageUrl) {
    return "";
  }

  return String(imageUrl)
    .split(",")[0]
    .trim()
    .replace(/^["']+|["']+$/g, "");
};

// =========================================================
// NORMALIZE RESTAURANT RESPONSE
// =========================================================

const normalizeAnlookRestaurant = (restaurant) => {
  const rating = Number(restaurant?.ratings);

  const startingPrice = Number(restaurant?.starting_price);

  const distance = Number(restaurant?.distance);

  const latitude = Number(restaurant?.latitude ?? restaurant?.lat);

  const longitude = Number(
    restaurant?.longitude ?? restaurant?.lontitude ?? restaurant?.lng,
  );

  return {
    id: restaurant?.pk_restaurant_id ?? restaurant?.user_id,

    categoryId: restaurant?.fk_category_id ?? null,

    userId: String(restaurant?.user_id || "").trim(),

    name: String(restaurant?.restaurant_name || "Restaurant").trim(),

    description: String(restaurant?.restaurant_description || "").trim(),

    image: restaurant?.restaurant_profile_image
      ? String(restaurant.restaurant_profile_image)
          .split(",")
          .map((url) => cleanImageUrl(url))
          .filter(Boolean)
      : [],

    address: String(restaurant?.restaurant_address || "").trim(),

    offer: String(restaurant?.restaurant_offer || "").trim(),

    servingType: String(restaurant?.serving_type || "")
      .trim()
      .toUpperCase(),

    rating: Number.isFinite(rating) ? rating : 0,

    startingPrice:
      Number.isFinite(startingPrice) && startingPrice >= 0
        ? startingPrice
        : null,

    openingTime: String(restaurant?.opening_time || "").trim(),

    closingTime: String(restaurant?.closing_time || "").trim(),

    distance: Number.isFinite(distance) ? distance : null,

    latitude: Number.isFinite(latitude) ? latitude : null,

    longitude: Number.isFinite(longitude) ? longitude : null,

    createdOn: restaurant?.created_on || null,

    raw: restaurant,
  };
};

// =========================================================
// MERGE RESTAURANTS
// =========================================================

const mergeRestaurants = (previousRestaurants, newRestaurants) => {
  const restaurantMap = new Map();

  [...previousRestaurants, ...newRestaurants].forEach((restaurant) => {
    const key = restaurant?.id || restaurant?.userId;

    if (!key) {
      return;
    }

    restaurantMap.set(String(key), restaurant);
  });

  return Array.from(restaurantMap.values());
};

// =========================================================
// SHARED RESTAURANT REQUEST CACHE
// =========================================================

// Same page par multiple components same API request karein,
// to actual network request sirf ek baar hogi.

const RESTAURANT_CACHE_TTL = 15000; // 15 seconds

const restaurantResponseCache = new Map();
const restaurantPendingRequests = new Map();

const createRestaurantRequestKey = (payload = {}) => {
  return JSON.stringify({
    customerLatitude: String(payload?.customerLatitude ?? "").trim(),

    customerLongitude: String(payload?.customerLongitude ?? "").trim(),

    limit: Number(payload?.limit || 10),

    offset: Number(payload?.offset || 0),

    range: Number(payload?.range || 10),

    serving_type: String(payload?.serving_type || "").trim(),

    best_of: String(payload?.best_of || "").trim(),
  });
};

// =========================================================
// RESTAURANT HOOK
// =========================================================

export const useRestaurant = () => {
  const { api, selectedDeliveryLocation } = useGlobalContext();

  const restaurantsRef = useRef([]);

  const [restaurants, setRestaurants] = useState([]);

  const [restaurantLoading, setRestaurantLoading] = useState(false);

  const [loadingMoreRestaurants, setLoadingMoreRestaurants] = useState(false);

  const [restaurantError, setRestaurantError] = useState("");

  const [hasMoreRestaurants, setHasMoreRestaurants] = useState(false);

  // =======================================================
  // UPDATE RESTAURANTS
  // =======================================================

  const updateRestaurants = useCallback((restaurantList) => {
    restaurantsRef.current = restaurantList;

    setRestaurants(restaurantList);
  }, []);

  // =======================================================
  // FETCH SINGLE PAGE
  // =======================================================

const fetchRestaurantPage = useCallback(
  async ({
    categoryIds = [],
    customerLatitude,
    customerLongitude,
    limit = 10,
    offset = 0,
    range = 10,
    servingType = "",
    bestOf = "",
    forceRefresh = false,
  }) => {
    if (!api) {
      throw new Error("Anlook API instance is not available.");
    }

    // =================================================
    // SELECTED DELIVERY ADDRESS COORDINATES
    // =================================================
    //
    // IMPORTANT:
    //
    // Do NOT use:
    //
    // customerLatitude
    // customerLongitude
    // navigator.geolocation
    // restaurant lat/lng
    //
    // Only the coordinates of the address selected
    // from AddressModal will be used.
    // =================================================
const latitude = String(
  customerLatitude ?? selectedDeliveryLocation?.latitude ?? "",
).trim();

const longitude = String(
  customerLongitude ?? selectedDeliveryLocation?.longitude ?? "",
).trim();

const latitudeNumber = Number(latitude);
const longitudeNumber = Number(longitude);

if (
  !latitude ||
  !longitude ||
  !Number.isFinite(latitudeNumber) ||
  !Number.isFinite(longitudeNumber) ||
  (latitudeNumber === 0 && longitudeNumber === 0)
) {
  throw new Error(
    "Valid latitude and longitude are required before loading restaurants.",
  );
}

    // =================================================
    // VALIDATE SELECTED LOCATION
    // =================================================

    if (
      !latitude ||
      !longitude ||
      !Number.isFinite(latitudeNumber) ||
      !Number.isFinite(longitudeNumber) ||
      (latitudeNumber === 0 && longitudeNumber === 0)
    ) {
      throw new Error(
        "Please select a valid delivery address before loading restaurants.",
      );
    }

    // =================================================
    // API PAYLOAD
    // =================================================

 const payload = {
   customerLatitude: latitude,
   customerLongitude: longitude,
   limit: Number(limit) || 10,
   offset: Number(offset) || 0,
   range: 10,
   serving_type: String(servingType || "").trim(),
   best_of: String(bestOf || "").trim(),
 };

    // =================================================
    // DEBUG
    // =================================================

    console.log("restaurantGetViaCategoryV1 PAYLOAD:", payload);

    console.log("SELECTED DELIVERY LOCATION:", {
      latitude,
      longitude,
    });

    // =================================================
    // SHARED REQUEST KEY
    // =================================================

    const requestKey = createRestaurantRequestKey(payload);

    const currentTime = Date.now();

    // =================================================
    // 1. CHECK EXISTING CACHE
    // =================================================

    const cachedRequest = restaurantResponseCache.get(requestKey);

    // ================================================
    // NORMAL CALL → CACHE ALLOWED
    // TRY AGAIN / FORCE → CACHE BYPASS
    // ================================================

    if (
      !forceRefresh &&
      cachedRequest &&
      currentTime - cachedRequest.timestamp < RESTAURANT_CACHE_TTL
    ) {
      console.log("RESTAURANT API CACHE HIT:", requestKey);

      return cachedRequest.data;
    }

    // Force refresh ya expired cache ko remove karo
    if (cachedRequest) {
      restaurantResponseCache.delete(requestKey);
    }

    // Expired cache remove
    if (cachedRequest) {
      restaurantResponseCache.delete(requestKey);
    }

    // =================================================
    // 2. SAME REQUEST ALREADY RUNNING
    // =================================================
    //
    // Example:
    //
    // RestaurantNear
    //     ↓
    // API started
    //
    // AllRestaurants
    //     ↓
    // same payload
    //
    // AllRestaurants new API hit nahi karega.
    // Existing promise ka result wait karega.
    // =================================================

    const existingRequest = restaurantPendingRequests.get(requestKey);

    if (!forceRefresh && existingRequest) {
      console.log("RESTAURANT API JOIN EXISTING REQUEST:", requestKey);

      return existingRequest;
    }

    // =================================================
    // 3. NEW NETWORK REQUEST
    // =================================================

    console.log("RESTAURANT API NETWORK CALL:", requestKey);

    const requestPromise = (async () => {
      try {
        const response = await api.post(
          "/Chaafo/restaurantGetViaCategoryV1",
          payload,
        );

        const responseData = response?.data;

       const responseCode = String(responseData?.responseCode || "").trim();

       // =====================================================
       // PAGINATION END
       // 02 = next page me koi restaurant nahi hai
       // Isko error treat nahi karna
       // =====================================================

       if (responseCode === "02") {
         console.log(" RESTAURANT PAGINATION COMPLETED");

         return [];
       }

       // =====================================================
       // ACTUAL API ERROR
       // =====================================================

       if (responseCode !== "00") {
         throw new Error(
           responseData?.responseMessage || "Unable to fetch all restaurants.",
         );
       }
        const restaurantList = Array.isArray(responseData?.response)
          ? responseData.response
          : [];

        const normalizedRestaurants = restaurantList.map(
          normalizeAnlookRestaurant,
        );

        // =================================================
        // SAVE RESPONSE
        // =================================================

        restaurantResponseCache.set(requestKey, {
          timestamp: Date.now(),
          data: normalizedRestaurants,
        });

        return normalizedRestaurants;
      } finally {
        // Request complete hone ke baad pending list se remove
        restaurantPendingRequests.delete(requestKey);
      }
    })();

    // =================================================
    // SAVE RUNNING REQUEST
    // =================================================

    restaurantPendingRequests.set(requestKey, requestPromise);

    return requestPromise;
  },
  [
    api,
    selectedDeliveryLocation?.latitude,
    selectedDeliveryLocation?.longitude,
  ],
);

  // =======================================================
  // GET RESTAURANTS
  // =======================================================

 const getRestaurants = useCallback(
   async ({
     categoryIds = [],
     customerLatitude,
     customerLongitude,
     limit = 10,
     offset = 0,
     range = 10,
     servingType = "",
     bestOf = "",
     append = false,
     forceRefresh = false,
   }) => {
     try {
       if (append) {
         setLoadingMoreRestaurants(true);
       } else {
         setRestaurantLoading(true);
       }

       setRestaurantError("");

   const restaurantList = await fetchRestaurantPage({
     categoryIds,
     customerLatitude,
     customerLongitude,
     limit,
     offset,
     range,
     servingType,
     bestOf,
     forceRefresh,
   });

       const updatedRestaurants = append
         ? mergeRestaurants(restaurantsRef.current, restaurantList)
         : restaurantList;

       updateRestaurants(updatedRestaurants);

       const hasMore = restaurantList.length >= Number(limit);

       setHasMoreRestaurants(hasMore);

       return {
         success: true,

         data: restaurantList,

         allData: updatedRestaurants,

         hasMore,
       };
     } catch (error) {
       console.error(
         "Anlook Restaurant API error:",
         error?.response?.data || error,
       );

       const message =
         error?.response?.data?.responseMessage ||
         error?.response?.data?.message ||
         error?.message ||
         "Unable to fetch all restaurants.";

       setRestaurantError(message);

       if (!append) {
         updateRestaurants([]);
       }

       return {
         success: false,

         data: [],

         hasMore: false,

         message,
       };
     } finally {
       setRestaurantLoading(false);

       setLoadingMoreRestaurants(false);
     }
   },
   [fetchRestaurantPage, updateRestaurants],
 );

  // =======================================================
  // GET ALL RESTAURANTS
  // =======================================================

  const getAllRestaurants = useCallback(
    async ({
      categoryIds = [],
      customerLatitude,
      customerLongitude,
      limit = 10,
      offset = 0,
      range = 10,
      servingType = "",
      bestOf = "",
      forceRefresh = false,
    }) => {
      try {
        setLoadingMoreRestaurants(true);

        setRestaurantError("");

        let allRestaurants = [...restaurantsRef.current];

        let offset = allRestaurants.length;

        let shouldContinue = true;

        let pageCount = 0;

        // Prevent infinite API loop
        const maximumPages = 25;

        while (shouldContinue && pageCount < maximumPages) {
          const restaurantPage = await fetchRestaurantPage({
            categoryIds,

            customerLatitude,
            customerLongitude,

            limit,
            offset,
            range,
            servingType,
            bestOf,
          });

          if (restaurantPage.length === 0) {
            shouldContinue = false;

            break;
          }

          allRestaurants = mergeRestaurants(allRestaurants, restaurantPage);

          offset += Number(limit);

          pageCount += 1;

          shouldContinue = restaurantPage.length >= Number(limit);
        }

        updateRestaurants(allRestaurants);

        setHasMoreRestaurants(false);

        return {
          success: true,

          data: allRestaurants,
        };
      } catch (error) {
        console.error(
          "Load all restaurants error:",
          error?.response?.data || error,
        );

        const message =
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to load all restaurants.";

        setRestaurantError(message);

        return {
          success: false,

          data: restaurantsRef.current,

          message,
        };
      } finally {
        setLoadingMoreRestaurants(false);
      }
    },
    [fetchRestaurantPage, updateRestaurants],
  );

  // =======================================================
  // RESET
  // =======================================================

  const resetRestaurants = useCallback(() => {
    updateRestaurants([]);

    setRestaurantError("");

    setHasMoreRestaurants(false);
  }, [updateRestaurants]);

  // =======================================================
  // RETURN
  // =======================================================

  return {
    restaurants,

    getRestaurants,

    getAllRestaurants,

    resetRestaurants,

    restaurantLoading,

    loadingMoreRestaurants,

    restaurantError,

    hasMoreRestaurants,

    setRestaurantError,
  };
};
